import {
  type MemberActivity,
  type Paginated,
  type User,
  type UserDetail,
  type UsersListQuerySchema,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';
import { type ApiSubscriber } from '@/shared/store/registry/api-registry/api-registry.types';

export type MembersListParams = z.input<typeof UsersListQuerySchema>;

/**
 * The members page's reads (P27c2). The lifecycle writes are the shared
 * `accountStatusApi`'s — the kit dialogs make them — and this slice hears
 * about them on the bus ({@link subscribeMembers}), which is how a write in
 * another slice reaches a list in this one (W5).
 */
export const membersApi = createApi({
  reducerPath: 'membersApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['MemberList', 'Member'],
  endpoints: (builder) => ({
    listMembers: builder.query<Paginated<User>, MembersListParams>({
      query: (params) => ({ url: '/admin/users', method: 'GET', params }),
      providesTags: ['MemberList'],
    }),

    memberDetail: builder.query<UserDetail, string>({
      query: (id) => ({ url: `/admin/users/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Member' as const, id }],
    }),

    /** Pointers into the member's history — counts and ids, read by the Activity tab on demand. */
    memberActivity: builder.query<MemberActivity, string>({
      query: (id) => ({ url: `/admin/users/${id}/activity`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Member' as const, id }],
    }),
  }),
});

export const subscribeMembers: ApiSubscriber = (dispatch) =>
  EVENT_BUS.subscribeTo('members.status.changed', ({ userId }) => {
    dispatch(membersApi.util.invalidateTags(['MemberList', { type: 'Member', id: userId }]));
  });

export const { useListMembersQuery, useMemberDetailQuery, useMemberActivityQuery } = membersApi;
